import type { ModuleKey, PermissionKey } from '@erp-boilerplate/types';

/**
 * The module registry.
 *
 * This array drives navigation, the generated route table and the route guards at
 * once. Adding a module is an entry here plus its actions in `MODULE_PERMISSIONS`
 * — never an edit to the router, because the router *generates* itself from this
 * list and pairs every path with `module.permission`. A module therefore cannot
 * be mounted without its guard.
 *
 * `icon` is a lucide-react icon *name*, resolved to a component on the web side
 * (`src/lib/icons.ts`). Keeping it a string is what lets this package stay
 * framework-agnostic and be imported by the API as well.
 */

export type NavGroupKey = 'general' | 'crm' | 'inventory' | 'sales' | 'finance' | 'admin';

export interface NavGroup {
  key: NavGroupKey;
  label: string;
  /** A group of one renders its single item flat, with no heading. */
  standalone?: boolean;
}

export const NAV_GROUPS: NavGroup[] = [
  { key: 'general', label: 'General', standalone: true },
  { key: 'crm', label: 'CRM' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'sales', label: 'Sales' },
  /**
   * Finance is its own group rather than another entry under Sales (Phase 8).
   * The ledger is not a selling screen: it reads what selling did, in a
   * vocabulary — accounts, periods, journals — that shares nothing with a
   * quotation or a dispatch note. Filing it under Sales would put a trial
   * balance one row below a buyback.
   */
  { key: 'finance', label: 'Finance' },
  { key: 'admin', label: 'Administration' },
];

export interface ModuleDefinition {
  key: ModuleKey;
  label: string;
  singular: string;
  path: string;
  /** A lucide-react icon name, resolved on the web side. */
  icon: string;
  group: NavGroupKey;
  order: number;
  /** The nav-guard permission. Holding it is what makes the item visible. */
  permission: PermissionKey;
  description: string;
  hiddenInNav?: boolean;
}

export const MODULES: ModuleDefinition[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    singular: 'Dashboard',
    path: '/dashboard',
    icon: 'LayoutDashboard',
    group: 'general',
    order: 1,
    permission: 'dashboard:view',
    description: 'Overview of the workspace.',
  },
  {
    key: 'customers',
    label: 'Customers',
    singular: 'Customer',
    path: '/customers',
    icon: 'Contact',
    group: 'crm',
    order: 10,
    permission: 'customers:view',
    description: 'The people you do business with.',
  },
  {
    /**
     * The social inbox: WhatsApp Business, Instagram and Messenger conversations
     * in one list, beside the leads they turn into.
     */
    key: 'inbox',
    label: 'Inbox',
    singular: 'Conversation',
    path: '/inbox',
    icon: 'MessagesSquare',
    group: 'crm',
    order: 12,
    permission: 'inbox:view',
    description: 'WhatsApp, Instagram and Messenger conversations.',
  },
  {
    key: 'leads',
    label: 'Leads',
    singular: 'Lead',
    path: '/leads',
    icon: 'Target',
    group: 'crm',
    order: 11,
    permission: 'leads:view',
    description: 'Enquiries and the sales pipeline.',
  },
  {
    key: 'appointments',
    label: 'Appointments',
    singular: 'Appointment',
    path: '/appointments',
    icon: 'CalendarClock',
    group: 'crm',
    order: 12,
    permission: 'appointments:view',
    description: 'Scheduled visits and trials.',
  },
  {
    key: 'products',
    label: 'Products',
    singular: 'Product',
    path: '/products',
    icon: 'Shirt',
    group: 'inventory',
    order: 20,
    permission: 'products:view',
    description: 'The commercial catalogue of bridal pieces.',
  },
  {
    key: 'product_items',
    label: 'Physical Items',
    singular: 'Physical Item',
    path: '/physical-items',
    icon: 'Boxes',
    group: 'inventory',
    order: 21,
    permission: 'product_items:view',
    description: 'Every physical bridal piece and its live status.',
  },
  {
    key: 'categories',
    label: 'Categories',
    singular: 'Category',
    path: '/categories',
    icon: 'LayoutGrid',
    group: 'inventory',
    order: 22,
    permission: 'categories:view',
    description: 'Product categories master.',
  },
  {
    key: 'inventory_locations',
    label: 'Locations',
    singular: 'Location',
    path: '/locations',
    icon: 'Warehouse',
    group: 'inventory',
    order: 23,
    permission: 'inventory_locations:view',
    description: 'Physical inventory locations.',
  },
  {
    key: 'quotations',
    label: 'Quotations',
    singular: 'Quotation',
    path: '/quotations',
    icon: 'FileText',
    group: 'sales',
    order: 30,
    permission: 'quotations:view',
    description: 'Price quotes for customers.',
  },
  {
    key: 'sales',
    label: 'Sales',
    singular: 'Sale',
    path: '/sales',
    icon: 'ShoppingBag',
    group: 'sales',
    order: 31,
    permission: 'sales:view',
    description: 'Confirmed sales of bridal pieces.',
  },
  {
    key: 'invoices',
    label: 'Invoices',
    singular: 'Invoice',
    path: '/invoices',
    icon: 'ReceiptText',
    group: 'sales',
    order: 32,
    permission: 'invoices:view',
    description: 'Printable invoices for sales.',
  },
  {
    key: 'payments',
    label: 'Payments',
    singular: 'Payment',
    path: '/payments',
    icon: 'Wallet',
    group: 'sales',
    order: 33,
    permission: 'payments:view',
    description: 'Receipts, collection and outstanding balances.',
  },
  {
    key: 'rentals',
    label: 'Rentals',
    singular: 'Rental Booking',
    path: '/rental-bookings',
    icon: 'CalendarRange',
    group: 'sales',
    order: 34,
    permission: 'rentals:view',
    description: 'Rental bookings and physical-item availability.',
  },
  {
    key: 'buybacks',
    label: 'Buybacks',
    singular: 'Buyback',
    path: '/buybacks',
    icon: 'Undo2',
    group: 'sales',
    order: 35,
    permission: 'buybacks:view',
    description: 'Buying a physical piece back from the customer who owns it.',
  },
  {
    /**
     * The ledger (Phase 8).
     *
     * One module entry for the whole accounting surface — chart of accounts,
     * fiscal periods, journals, ledger and the three statements — because they
     * are one book kept by one function, sharing one permission namespace. The
     * alternative, seven `ModuleKey`s, would fragment RBAC into seven permission
     * sets for a job that is done by one person, which is the same reasoning
     * that keeps the four rental stages under `rentals`.
     */
    key: 'accounting',
    label: 'Accounting',
    singular: 'Accounting',
    path: '/accounting',
    icon: 'BookOpen',
    group: 'finance',
    order: 40,
    permission: 'accounting:view',
    description: 'Chart of accounts, journals, ledger and financial statements.',
  },
  {
    /**
     * Suppliers (Phase 11). Master data, filed beside the catalogue it stocks
     * rather than in Finance: the person who decides which supplier a lehenga
     * comes from is buying garments, not keeping books.
     */
    key: 'vendors',
    label: 'Vendors',
    singular: 'Vendor',
    path: '/vendors',
    icon: 'Factory',
    group: 'inventory',
    order: 18,
    permission: 'vendors:view',
    description: 'Suppliers the showroom buys stock from.',
  },
  {
    /**
     * Purchases (Phase 11).
     *
     * In Inventory rather than Finance, because posting one is a stockroom
     * action: the garments are physically in the building and somebody is
     * checking them in. The accounting it raises is a consequence, and it shows
     * up in the ledger where the ledger lives.
     */
    key: 'purchases',
    label: 'Purchases',
    singular: 'Purchase',
    path: '/purchases',
    icon: 'PackagePlus',
    group: 'inventory',
    order: 19,
    permission: 'purchases:view',
    description: 'Stock bought from a vendor, and the inventory it receives.',
  },
  {
    /**
     * Purchase returns (Phase 12).
     *
     * Beside purchases in Inventory, for the same reason: handing garments back
     * to a supplier is a stockroom action. The payable it reduces and the credit
     * it may raise show up in Payables.
     */
    key: 'purchase_returns',
    label: 'Purchase Returns',
    singular: 'Purchase Return',
    path: '/purchase-returns',
    icon: 'PackageMinus',
    group: 'inventory',
    order: 20,
    permission: 'purchase_returns:view',
    description: 'Goods handed back to a vendor, and the payable or credit that follows.',
  },
  {
    /**
     * Accounts payable (Phase 11).
     *
     * In Finance, unlike the two above, and the split is the segregation of
     * duties the module exists for: receiving stock and releasing money to the
     * supplier are different jobs done by different people.
     */
    key: 'payables',
    label: 'Payables',
    singular: 'Payable',
    path: '/payables',
    icon: 'HandCoins',
    group: 'finance',
    order: 41,
    permission: 'payables:view',
    description: 'What the business owes its suppliers, and paying it.',
  },
  {
    /**
     * The notification centre (Phase 7).
     *
     * In Administration rather than Sales: it is a communication *monitor* —
     * what the showroom told which customer, down which channel, and whether it
     * got there — not a place where selling happens. The bell in the top bar is
     * the everyday way in; this entry is for the person who has to go and look
     * at why a batch of emails failed.
     */
    key: 'notifications',
    label: 'Notifications',
    singular: 'Notification',
    path: '/notifications',
    icon: 'Bell',
    group: 'admin',
    order: 94,
    permission: 'notifications:view',
    description: 'What customers were told, and whether it reached them.',
  },
  {
    key: 'users',
    label: 'Users',
    singular: 'User',
    path: '/users',
    icon: 'Users',
    group: 'admin',
    order: 90,
    permission: 'users:view',
    description: 'People with access.',
  },
  {
    key: 'roles',
    label: 'Roles',
    singular: 'Role',
    path: '/roles',
    icon: 'ShieldCheck',
    group: 'admin',
    order: 91,
    permission: 'roles:view',
    description: 'Permission sets.',
  },
  {
    key: 'audit_logs',
    label: 'Audit Log',
    singular: 'Audit Entry',
    path: '/audit-logs',
    icon: 'History',
    group: 'admin',
    order: 92,
    permission: 'audit_logs:view',
    description: 'Who did what, when.',
  },
  {
    key: 'lead_sources',
    label: 'Lead Sources',
    singular: 'Lead Source',
    path: '/lead-sources',
    icon: 'Tags',
    group: 'admin',
    order: 93,
    permission: 'lead_sources:view',
    description: 'Where enquiries come from.',
  },
  {
    /**
     * Lead sources and channels: the website enquiry form, Facebook/Instagram
     * Lead Ads, WhatsApp Business and Meta messaging connections.
     */
    key: 'integrations',
    label: 'Lead Sources & Channels',
    singular: 'Connection',
    path: '/integrations',
    icon: 'Plug',
    group: 'admin',
    order: 96,
    permission: 'integrations:view',
    description: 'Website enquiry form, Facebook/Instagram Lead Ads, WhatsApp and Meta messaging.',
  },
  {
    /**
     * Company settings: the business profile and how its documents print —
     * GSTIN, bank and UPI details, signature, footer, and a preset look per
     * document type. Read by every printed document through the document helper.
     */
    key: 'company_settings',
    label: 'Company Settings',
    singular: 'Company Setting',
    path: '/company-settings',
    icon: 'Settings',
    group: 'admin',
    order: 95,
    permission: 'company_settings:view',
    description: 'Business details and document printing.',
  },
];

export const getModule = (key: ModuleKey): ModuleDefinition | undefined =>
  MODULES.find((module) => module.key === key);

export const getNavGroup = (key: NavGroupKey): NavGroup | undefined =>
  NAV_GROUPS.find((group) => group.key === key);

export const getModulesByGroup = (group: NavGroupKey): ModuleDefinition[] =>
  MODULES.filter((module) => module.group === group && !module.hiddenInNav).sort(
    (a, b) => a.order - b.order,
  );

/**
 * Longest path first, so `/inventory/transfers` resolves to the transfers module
 * rather than to `/inventory` — a plain `startsWith` scan would pick whichever
 * came first in the array.
 */
export const getModuleByPath = (path: string): ModuleDefinition | undefined =>
  [...MODULES]
    .sort((a, b) => b.path.length - a.path.length)
    .find((module) => path === module.path || path.startsWith(`${module.path}/`));

export type NavNode =
  | { type: 'item'; module: ModuleDefinition }
  | { type: 'group'; group: NavGroup; modules: ModuleDefinition[] };

/**
 * The sidebar tree, filtered by what the viewer may open.
 *
 * A group whose every module is hidden does not render its heading either — an
 * empty "Administration" label reads as a broken screen rather than as a
 * permission boundary.
 */
export const buildNavTree = (canAccess: (module: ModuleDefinition) => boolean): NavNode[] => {
  const nodes: NavNode[] = [];

  for (const group of NAV_GROUPS) {
    const modules = getModulesByGroup(group.key).filter(canAccess);
    if (modules.length === 0) continue;

    if (group.standalone) {
      for (const module of modules) nodes.push({ type: 'item', module });
    } else {
      nodes.push({ type: 'group', group, modules });
    }
  }

  return nodes;
};
